/**
 * Subject palette for the Free PEP Readiness Check.
 *
 * Same colours the student app uses (scripts/design/DESIGN_SYSTEM.md): Mathematics blue,
 * Language Arts emerald, Science orange, Social Studies violet, Ability Test rose. Class names
 * are written out in full so Tailwind keeps them.
 */
import { Calculator, BookOpen, FlaskConical, Globe2, Brain, type LucideIcon } from "lucide-react";

export interface SubjectStyle {
  card: string;
  sticker: string;
  chip: string;
  bar: string;
  track: string;
  button: string;
  text: string;
  icon: LucideIcon;
}

const STYLES: Record<string, SubjectStyle> = {
  "Mathematics": {
    card: "bg-blue-500/10 border-blue-500/25", sticker: "bg-blue-500 text-white",
    chip: "bg-blue-500/15 text-blue-800", bar: "bg-blue-500", track: "bg-blue-500/15",
    button: "bg-blue-500 hover:bg-blue-600 text-white", text: "text-blue-900/70", icon: Calculator,
  },
  "Language Arts": {
    card: "bg-emerald-500/10 border-emerald-500/25", sticker: "bg-emerald-500 text-white",
    chip: "bg-emerald-500/15 text-emerald-800", bar: "bg-emerald-500", track: "bg-emerald-500/15",
    button: "bg-emerald-500 hover:bg-emerald-600 text-white", text: "text-emerald-900/70", icon: BookOpen,
  },
  "Science": {
    card: "bg-orange-500/10 border-orange-500/25", sticker: "bg-orange-500 text-white",
    chip: "bg-orange-500/15 text-orange-800", bar: "bg-orange-500", track: "bg-orange-500/15",
    button: "bg-orange-500 hover:bg-orange-600 text-white", text: "text-orange-900/70", icon: FlaskConical,
  },
  "Social Studies": {
    card: "bg-violet-500/10 border-violet-500/25", sticker: "bg-violet-500 text-white",
    chip: "bg-violet-500/15 text-violet-800", bar: "bg-violet-500", track: "bg-violet-500/15",
    button: "bg-violet-500 hover:bg-violet-600 text-white", text: "text-violet-900/70", icon: Globe2,
  },
  "Ability Test": {
    card: "bg-rose-500/10 border-rose-500/25", sticker: "bg-rose-500 text-white",
    chip: "bg-rose-500/15 text-rose-800", bar: "bg-rose-500", track: "bg-rose-500/15",
    button: "bg-rose-500 hover:bg-rose-600 text-white", text: "text-rose-900/70", icon: Brain,
  },
};

const FALLBACK: SubjectStyle = {
  card: "bg-primary/10 border-primary/25", sticker: "bg-primary text-primary-foreground",
  chip: "bg-primary/15 text-primary", bar: "bg-primary", track: "bg-primary/15",
  button: "bg-primary hover:bg-primary/90 text-primary-foreground", text: "text-primary/80", icon: BookOpen,
};

export function subjectStyle(subject: string | null | undefined): SubjectStyle {
  return (subject && STYLES[subject]) || FALLBACK;
}

/** Band colours, in the same flat language as the subject cards. */
export function bandStyle(band: string): { chip: string; bar: string; track: string } {
  switch (band) {
    case "Highly Proficient": return { chip: "bg-emerald-500/15 text-emerald-800", bar: "bg-emerald-500", track: "bg-emerald-500/15" };
    case "Proficient": return { chip: "bg-blue-500/15 text-blue-800", bar: "bg-blue-500", track: "bg-blue-500/15" };
    case "Developing": return { chip: "bg-amber-500/15 text-amber-800", bar: "bg-amber-500", track: "bg-amber-500/15" };
    default: return { chip: "bg-rose-500/15 text-rose-800", bar: "bg-rose-500", track: "bg-rose-500/15" };
  }
}
